import { useCallback, useMemo, useState } from "react";

import { oidcAuthority, oidcClientId } from "authentication/constants";
import { parseOidcUser } from "authentication/parseOidcUser";
import type { LanguageCode } from "common/types/languageCode";
import { isLanguageCode } from "common/utilities/isLanguageCode";
import { safeJsonParse } from "common/utilities/safeJsonParse";
import type { CallToActionId } from "domains/callsToAction/types";
import type { PayloadTerm } from "domains/language/types";
import type { Theme } from "domains/theme/types";
import type { PermissionData } from "entities/permissions/types";

type LocalStorage = {
  APP_BANNER_USER_CLOSED: true;
  COACHMARKS_VIEWED_ITEMS: string[];
  COACHMARKS_VISIBILITY_STATUS: boolean | null;
  DISMISSED_INCIDENTS_STORAGE_KEY: string[];
  dismissedCallsToAction: [CallToActionId, number][];
  language: LanguageCode;
  permissions: PermissionData;
  terms: PayloadTerm[];
  theme: Theme;
  topic_detail_call_to_action_banner: number;
};

export const readFromLocalStorage = <T extends keyof LocalStorage>(key: T) => {
  const value = localStorage.getItem(key);
  if (value === null) return undefined;
  // Remove this code in December
  if (key === "language" && isLanguageCode(value)) {
    writeToLocalStorage("language", value);
    return value as LocalStorage[T];
  }
  const result = safeJsonParse(value);
  if (result === undefined) return undefined;
  return result.value as LocalStorage[T];
};

export const writeToLocalStorage = <T extends keyof LocalStorage>(key: T, value: LocalStorage[T]) =>
  localStorage.setItem(key, JSON.stringify(value));

export const removeFromLocalStorage = (key: keyof LocalStorage) => localStorage.removeItem(key);

export const readOidcUserFromLocalStorage = () => {
  const value = localStorage.getItem(`oidc.user:${oidcAuthority}:${oidcClientId}`);
  return value === null ? undefined : parseOidcUser(value);
};

export const useLocalStorageState = <T extends keyof LocalStorage>(key: T) => {
  const [value, setValue] = useState(() => readFromLocalStorage(key));

  const setNewValue = useCallback(
    (newValue: LocalStorage[T]) => {
      setValue(newValue);
      writeToLocalStorage(key, newValue);
    },
    [key],
  );

  return useMemo(() => [value, setNewValue] as const, [setNewValue, value]);
};
